// --- UPDATED FILE: packages/agent-kit/src/hooks/useQuoteGenerator.ts ---

import { useState, useCallback } from 'react';
import { quotesService, isApiError } from '@dopomogai/api-client';
import { AgentKitError } from '../../types';
// --- MODIFIED: Import AgentVersionOverrides ---
import type { Quote, UUID, AgentVersionOverrides } from '@dopomogai/types';
import { useAnalytics } from '../../analytics/useAnalytics';

// --- MODIFIED: Update options type ---
interface GenerateQuoteOptions {
  discount_code?: string;
  overrides?: Partial<AgentVersionOverrides>; // Pass a patch of the overrides
}

/**
 * A hook for triggering the creation of a new, signed quote from an agent version.
 * It handles mutation state (loading, error) and provides a clear function to call.
 * 
 * Crucially, it also detects when the backend requires an email to proceed (HTTP 428)
 * and exposes an `isEmailRequired` state for the UI to handle this.
 */
export const useQuoteGenerator = () => {
  const analytics = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  
  const [isEmailRequired, setIsEmailRequired] = useState(false);

  /**
   * Clears the "email required" flag. The UI should call this after the user
   * has provided their email and the component is ready for another attempt.
   */
  const clearEmailRequiredFlag = useCallback(() => {
    setIsEmailRequired(false);
  }, []);

  // --- MODIFIED: Update function signature ---
  const generateQuote = useCallback(
    async (versionId: UUID, opts?: GenerateQuoteOptions): Promise<Quote | null> => {
      setIsLoading(true);
      setError(null);
      setQuote(null);
      setIsEmailRequired(false);

      analytics.track('quote_generate_request', { version_id: versionId });

      try {
        // --- MODIFIED: The opts object (containing overrides) is passed directly ---
        const response = await quotesService.createQuote(versionId, opts);

        if (isApiError(response)) {
          // ... (rest of the logic is the same)
          if (response.status === 428) {
            setIsEmailRequired(true);
            analytics.track('quote_blocked_email_required', { version_id: versionId });
            return null;
          }
          const err = new AgentKitError('Failed to generate quote', response);
          setError(err);
          analytics.track('quote_failed', {
            version_id: versionId,
            error_code: response.status,
            http_status: response.status,
          });
          return null;
        }

        analytics.track('quote_generated', {
          quote_id: response.id,
          version_id: versionId,
          total_usd: response.totals.setup_usd,
        });
        setQuote(response);
        return response;

      } catch (e: any) {
        // Handle network or other unexpected errors.
        const err = new AgentKitError('A network error occurred while generating the quote.', e);
        setError(err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [analytics]
  );

  return {
    generateQuote,
    quote,
    isLoading,
    error,
    isEmailRequired,
    clearEmailRequiredFlag,
  };
};